import { api } from '@/lib/http-client';
import type {
  AsistentePreview,
  PromptFinalPreview,
  Settings,
  UpdateSettingsRequest,
} from '@/types/settings';
import { uploadProductPhoto } from './products';
import type { ProductPhotoUpload, UploadProductPhotoRequest } from '@/types/products';

/**
 * Módulo 2 — Ajustes generales del negocio (`docs/openapi.yaml` → tag `Settings`).
 *
 * `GET` lo puede pedir cualquier miembro del tenant; `PATCH` es solo ADMIN y crea la fila
 * si el negocio todavía no tenía ajustes guardados.
 */

/** `GET /api/tenants/:tenantId/settings` — objeto por defecto si el tenant no tiene fila. */
export function fetchSettings(tenantId: string): Promise<Settings> {
  return api.get<Settings>(`/api/tenants/${tenantId}/settings`);
}

/** `PATCH /api/tenants/:tenantId/settings` — exige al menos un campo (null = vaciar). */
export function updateSettings(
  tenantId: string,
  payload: UpdateSettingsRequest,
): Promise<Settings> {
  return api.patch<Settings>(`/api/tenants/${tenantId}/settings`, payload);
}

/**
 * `POST /api/tenants/:tenantId/products/fotos` — sube UNA imagen y devuelve su URL.
 *
 * Se reutiliza ese endpoint para la **guía de tallas del negocio** (Ajustes → General, 24-sep-2026): es el
 * mismo caso (una imagen del negocio, hasta 2 MB, guardada en disco) y así no hay una ruta nueva que
 * mantener. El archivo queda en `uploads/productos/<tenantId>/` y lo que se guarda en
 * `settings.guiaTallasUrl` es la URL que devuelve.
 */
export function uploadSizeChart(
  tenantId: string,
  payload: UploadProductPhotoRequest,
): Promise<ProductPhotoUpload> {
  return uploadProductPhoto(tenantId, payload);
}

/**
 * `GET /api/tenants/:tenantId/settings/prompt-final?asistente=…` (22-sep-2026) — el prompt que recibe
 * el modelo, armado por el backend. Se pide al abrir «Ver el prompt final» y enseña lo ÚLTIMO guardado.
 */
export function fetchPromptFinal(
  tenantId: string,
  asistente: AsistentePreview = 'VENTAS',
): Promise<PromptFinalPreview> {
  return api.get<PromptFinalPreview>(`/api/tenants/${tenantId}/settings/prompt-final`, {
    query: { asistente },
  });
}
